/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { isEqual, startCase, toLower } from 'lodash';
import orderBy from 'lodash/orderBy';
import { sortList } from '../../actions/workPackageActions';
import {
  setCurrentTeams,
  setCurrentProducts
} from '../../actions/defaultOptionsActions';
import { workPackagesWithChildren } from '../../selectors/workPackageSelector';
import { filterTargetsBySpace } from '../../selectors/targetSelector';
import {
  WorkPackageStatuses,
  HigherLevelStatuses
} from '../../constants/storyStatusTypes';
import VrsFilterDropdown from './vrsFilterDropdown';
import ValueFilterDropdown from './valueFilterDropdown';
import SprintFilter from './sprintFilter';
import TargetFilter from './targetFilter';
import ProductFilter from './productFilter';
import StateFilter from './stateFilter';
import ListFilter from './listFilter';
import DefaultFilter from './defaultFilter';
import Table from './table';
import { extractImmediateDescendants } from '../../utils/hierarchyHelpers';
import { authorizedWorkersForSpace } from '../../utils/userHelpers';
import { currentOrganization } from '../../utils/organizationHelpers';
import { filterActiveCadences } from '../../selectors/cadenceSelector';

class TableList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      sortDescending: false,
      sortField: 'rank',
      vrsFilter: {
        risk: [],
        value: [],
        size: [],
        rroe: [],
        tc: []
      },
      sprintFilter: [],
      targetFilter: [],
      stateFilter: [],
      listTeamFilter: [],
      parentFilter: [],
      ownerFilter: [],
      creatorFilter: [],
      teamFilter: [],
      blockedFilter: -1,
      typeFilter: [],
      listProductFilter: [],
      showHolon: false,
      dropDown: {}
    };
  }

  componentWillMount() {
    if (!this.props.noPreserve) this.processComponentState();
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.level !== nextProps.level) {
      this.processComponentState();
    }
  }

  // the below code breaks filtering/soring functionality as
  // a user moves between levels on planwall

  // shouldComponentUpdate(nextProps) {
  //   const { preservedState } = this.props;
  //   if (!isEqual(preservedState, nextProps.preservedState)) {
  //     return false;
  //   }
  //   return true;
  // }

  componentDidUpdate(prevProps) {
    const { sortField } = this.state;
    const { preservedState, noPreserve, tableFields } = this.props;

    // Set sort field back to rank if user hide column sorted
    if (!isEqual(tableFields, prevProps.tableFields)) {
      const field = tableFields.find((tf) => tf.source === sortField);
      if (field && !field.visible) {
        this.setState(
          { sortField: 'rank', sortDescending: false },
          this.saveComponentState
        );
      }
    }

    if (!isEqual(preservedState, prevProps.preservedState) && !noPreserve) {
      this.processComponentState();
    }
  }

  setDropdown = (updatedDropDown) => {
    this.setState({ dropDown: updatedDropDown });
  };

  processComponentState = () => {
    const { preservedState, level, tableFields } = this.props;
    if (!preservedState) return;
    const sort = preservedState.sort
      ? window.atob(preservedState.sort).split(',')
      : [];
    const filters = preservedState.fil ? window.atob(preservedState.fil) : '{}';
    const newSortField = sort ? sort[0] : undefined;
    const newSortDescending = sort && sort[1] ? JSON.parse(sort[1]) : false;
    const parsedFilters = JSON.parse(filters);
    const newVrsFilter = parsedFilters.vrsFilter || {
      risk: [],
      value: [],
      size: [],
      rroe: [],
      tc: []
    };
    const newSprintFilter =
      (tableFields.find(
        (f) => f.name.toLowerCase() === 'sprint' && f.visible
      ) &&
        level === 1 &&
        parsedFilters.sprintFilter) ||
      [];
    const newTargetFilter =
      (tableFields.find(
        (f) => f.name.toLowerCase() === 'target' && f.visible
      ) &&
        level < 4 &&
        parsedFilters.targetFilter) ||
      [];
    const newStateFilter =
      (tableFields.find((f) => f.name.toLowerCase() === 'state' && f.visible) &&
        parsedFilters.stateFilter) ||
      [];
    const newListTeamFilter =
      (tableFields.find((f) => f.name.toLowerCase() === 'team' && f.visible) &&
        level < 3 &&
        parsedFilters.listTeamFilter) ||
      [];
    const newListProductFilter =
      (tableFields.find(
        (f) => f.name.toLowerCase() === 'product' && f.visible
      ) &&
        level <= 4 &&
        parsedFilters.listProductFilter) ||
      [];
    const newOwnerFilter =
      (tableFields.find((f) => f.name.toLowerCase() === 'owner' && f.visible) &&
        level <= 4 &&
        parsedFilters.ownerFilter) ||
      [];

    const newTypeFilter =
      (tableFields.find((f) => f.name.toLowerCase() === 'type' && f.visible) &&
        level <= 4 &&
        parsedFilters.typeFilter) ||
      [];

    const newCreatorFilter =
      (tableFields.find(
        (f) => f.name.toLowerCase() === 'created by' && f.visible
      ) &&
        level <= 4 &&
        parsedFilters.creatorFilter) ||
      [];

    const newBlockedFilter =
      (tableFields.find(
        (f) => f.name.toLowerCase() === 'blocked' && f.visible
      ) &&
        level <= 4 &&
        parsedFilters.blockedFilter) ||
      -1;

    const newParentFilter = parsedFilters.parentFilter || [];
    const newTeamFilter = parsedFilters.teamFilter || [];
    const newShowCompleted = preservedState.sc;
    const newState = {
      sortField: newSortField,
      sortDescending: newSortDescending,
      vrsFilter: newVrsFilter,
      sprintFilter: newSprintFilter,
      targetFilter: newTargetFilter,
      stateFilter: newStateFilter,
      parentFilter: newParentFilter,
      teamFilter: newTeamFilter,
      showCompleted: newShowCompleted,
      listTeamFilter: newListTeamFilter,
      listProductFilter: newListProductFilter,
      ownerFilter: newOwnerFilter,
      typeFilter: newTypeFilter,
      creatorFilter: newCreatorFilter,
      blockedFilter: newBlockedFilter
    };
    // Compare new state with current state and in case of one of them be
    // different it update state
    const shouldUpdateState = Object.keys(newState).some(
      (key) => !isEqual(newState[key], this.state[key])
    );
    if (shouldUpdateState) {
      this.setState((prevState) => ({ ...prevState, ...newState }));
    }
  };

  saveComponentState = () => {
    const { tableFields } = this.props;
    const {
      vrsFilter,
      sprintFilter,
      targetFilter,
      stateFilter,
      parentFilter,
      teamFilter,
      listTeamFilter,
      listProductFilter,
      ownerFilter,
      sortField,
      sortDescending,
      creatorFilter,
      blockedFilter,
      typeFilter
    } = this.state;
    const visibleFields = tableFields.reduce((of, field) => {
      const objectFields = of;
      objectFields[field.name.toLowerCase()] = field.visible;
      return objectFields;
    }, {});

    const savedState = {
      ...this.props.preservedState,
      col: window.btoa(JSON.stringify(visibleFields)),
      sort: window.btoa([sortField, sortDescending]),
      fil: window.btoa(
        JSON.stringify({
          vrsFilter,
          sprintFilter,
          targetFilter,
          stateFilter,
          parentFilter,
          teamFilter,
          listTeamFilter,
          listProductFilter,
          ownerFilter,
          creatorFilter,
          blockedFilter,
          typeFilter
        })
      )
    };
    if (!this.props.noPreserve) this.props.preserveComponentState(savedState);
  };

  activateSort = (field) => {
    let criteria = field;
    if (this.state.sortField === criteria) criteria = 'rank';
    const ascending =
      criteria === 'rank' || criteria === 'name' || criteria === 'description';

    this.setState(
      {
        sortField: criteria,
        sortDescending: !ascending
      },
      () => {
        if (!this.props.noPreserve) this.saveComponentState();
      }
    );
  };

  handleSorting = (workPackages) => {
    const order = this.state.sortDescending ? 'desc' : 'asc';
    const sorted = orderBy(workPackages, this.state.sortField, order);
    return sorted;
  };

  buildVrsFilter = (action, type, value) => {
    const { vrsFilter } = this.state;
    const { defaultOptions, level } = this.props;

    let defaultType;
    if (['rroe', 'tc'].includes(type)) defaultType = 'risk';
    else if (type === 'size' && level > 1) defaultType = 't_shirt_size';
    else defaultType = type;

    const defaults = defaultOptions[`${defaultType}_defaults`];
    const fieldDefaults = defaults ? defaults.values : {};
    const severityOptions = defaultOptions.severity_defaults
      ? defaultOptions.severity_defaults.values
      : {};
    const severityFilters = Object.keys(severityOptions).map(
      (s) => `severity-${s}`
    );
    const allOptions =
      type === 'value' && level === 1
        ? Object.keys(fieldDefaults).concat(severityFilters)
        : Object.keys(fieldDefaults);
    const filterByVrs = vrsFilter[type] || [];
    const index = filterByVrs.findIndex((item) => item === value);
    switch (action) {
      case 'all':
        if (filterByVrs.length === allOptions.length + 1) {
          vrsFilter[type] = [];
        } else {
          vrsFilter[type] = allOptions;
          vrsFilter[type].push('unassigned');
        }
        break;
      case 'add':
        vrsFilter[type] = vrsFilter[type]
          ? vrsFilter[type].concat(value)
          : [value];
        break;
      case 'remove':
        vrsFilter[type].splice(index, 1);
        break;
      default:
        vrsFilter[type] = [];
    }
    this.setState({ vrsFilter }, () => {
      if (!this.props.noPreserve) this.saveComponentState();
    });
  };

  filterByVrs = ({ result_type: resultType }) => {
    const { vrsFilter } = this.state;
    const filterKeys = Object.keys(vrsFilter);

    const matchWithResultAttribute = (filterKey) => {
      if (vrsFilter[filterKey].length === 0) return true;

      const resultKind = resultType.kind;
      let resultAttribute = resultType.type_attributes[filterKey];
      resultAttribute = resultAttribute && resultAttribute.toString();

      const isUnassignedSelected =
        vrsFilter[filterKey].includes('unassigned') && !resultAttribute;

      if (resultKind === 'Defect' && filterKey === 'value') {
        if (vrsFilter[filterKey].includes(`severity-${resultAttribute}`))
          return true;
        return isUnassignedSelected;
      }
      if (vrsFilter[filterKey].includes(resultAttribute)) return true;
      return isUnassignedSelected;
    };

    return filterKeys.every(matchWithResultAttribute);
  };

  handleVrsFilterClick = (type) => {
    const { vrsFilter } = this.state;
    const { defaultOptions, level } = this.props;
    let defaultType;
    if (['rroe', 'tc'].includes(type)) defaultType = 'risk';
    else if (type === 'size' && level > 1) defaultType = 't_shirt_size';
    else defaultType = type;

    const defaults = defaultOptions[`${defaultType}_defaults`];
    const fieldDefaults = defaults ? defaults.values : {};
    const severityOptions = defaultOptions.severity_defaults
      ? defaultOptions.severity_defaults.values
      : {};
    const severityFilters = Object.keys(severityOptions).map(
      (s) => `severity-${s}`
    );
    const allOptions =
      type === 'value' && level === 1
        ? Object.keys(fieldDefaults).concat(severityFilters)
        : Object.keys(fieldDefaults);

    if (
      vrsFilter[type] &&
      vrsFilter[type].length === Object.keys(allOptions).length + 1
    ) {
      vrsFilter[type] = [];
      this.setState({ vrsFilter });
    }
  };

  buildFilterBySprint = (sprint) => {
    const { sprintFilter } = this.state;
    const { cadences } = this.props;
    let newSprintFilter = JSON.parse(JSON.stringify(sprintFilter));

    if (sprint.target && sprint.target.type === 'button') {
      if (newSprintFilter.length === cadences.length + 1) {
        newSprintFilter = [];
      } else {
        newSprintFilter = cadences.map((sp) => sp.id);
        newSprintFilter.push('unassigned');
      }
    } else if (sprintFilter.includes(sprint)) {
      newSprintFilter = sprintFilter.filter((id) => id !== sprint);
    } else {
      newSprintFilter.push(sprint);
    }

    this.setState({ sprintFilter: newSprintFilter }, () => {
      if (!this.props.noPreserve) this.saveComponentState();
    });
  };

  filterBySprint = (result) => {
    const selectedSprints = this.state.sprintFilter;
    let includes = true;
    if (selectedSprints.length > 0) {
      const currentCadenceId = result.current_cadence
        ? result.current_cadence.id
        : result.current_cadence_id;
      includes =
        currentCadenceId &&
        selectedSprints.some((selected) => currentCadenceId === selected);
      if (
        selectedSprints.includes('unassigned') &&
        !result.cadence_ids.length
      ) {
        includes = true;
      }
    }
    return includes;
  };

  handleSprintFilterClick = () => {
    const { sprintFilter } = this.state;
    const { cadences } = this.props;

    if (sprintFilter.length === Object.keys(cadences).length + 1)
      this.setState({ sprintFilter: [] });
  };

  buildFilterByTarget = (option) => {
    const { targetFilter } = this.state;
    const { targets } = this.props;
    let newTargetFilter = [...targetFilter];

    if (option.target && option.target.type === 'button') {
      if (newTargetFilter.length === targets.length + 1) {
        newTargetFilter = [];
      } else {
        newTargetFilter = targets.map((target) => target.id);
        newTargetFilter.push('unassigned');
      }
    } else if (targetFilter.includes(option)) {
      newTargetFilter = targetFilter.filter((id) => id !== option);
    } else {
      newTargetFilter.push(option);
    }

    this.setState({ targetFilter: newTargetFilter }, () => {
      if (!this.props.noPreserve) this.saveComponentState();
    });
  };

  handleTargetFilterClick = () => {
    const { targetFilter } = this.state;
    const { targets } = this.props;

    if (targetFilter.length === Object.keys(targets).length + 1)
      this.setState({ targetFilter: [] });
  };

  buildFilterByListProduct = (option) => {
    const { listProductFilter } = this.state;
    const { currentProducts } = this.props;
    let newListProductFilter = [...listProductFilter];

    if (option.target && option.target.type === 'button') {
      if (newListProductFilter.length === currentProducts.length + 1) {
        newListProductFilter = [];
      } else {
        newListProductFilter = currentProducts.map((product) => product.id);
        newListProductFilter.push('unassigned');
      }
    } else if (listProductFilter.includes(option)) {
      newListProductFilter = listProductFilter.filter((id) => id !== option);
    } else {
      newListProductFilter.push(option);
    }

    this.setState({ listProductFilter: newListProductFilter }, () => {
      if (!this.props.noPreserve) this.saveComponentState();
    });
  };

  handleListProductFilterClick = () => {
    const { listProductFilter } = this.state;
    const { currentProducts } = this.props;
    if (listProductFilter.length === Object.keys(currentProducts).length)
      this.setState({ listProductFilter: [] });
  };

  filterByListProduct = (result) => {
    const { listProductFilter } = this.state;
    let includes = true;
    if (listProductFilter.length > 0) {
      includes = listProductFilter.includes(
        result.product_id ? result.product_id : 'unassigned'
      );
    }
    return includes;
  };

  buildFilterByListTeam = (option) => {
    const { listTeamFilter } = this.state;
    const { currentTeams } = this.props;
    let newListTeamFilter = [...listTeamFilter];
    if (option && option.target && option.target.type === 'button') {
      if (newListTeamFilter.length === currentTeams.length) {
        newListTeamFilter = [];
      } else {
        newListTeamFilter = currentTeams.map((team) => team.id);
      }
    } else if (listTeamFilter.includes(option)) {
      newListTeamFilter = listTeamFilter.filter((id) => id !== option);
    } else {
      newListTeamFilter.push(option);
    }
    this.setState({ listTeamFilter: newListTeamFilter }, () => {
      if (!this.props.noPreserve) this.saveComponentState();
    });
  };

  handleListTeamFilterClick = () => {
    const { listTeamFilter } = this.state;
    const { currentTeams } = this.props;
    if (listTeamFilter.length === Object.keys(currentTeams).length)
      this.setState({ listTeamFilter: [] });
  };

  filterByListTeam = (result) => {
    const { listTeamFilter } = this.state;
    let includes = true;
    if (listTeamFilter.length > 0) {
      includes = listTeamFilter.includes(
        result.team_id ? result.team_id : 'unassigned'
      );
    }
    return includes;
  };

  filterByTarget = (result) => {
    const selectedTargets = this.state.targetFilter;
    let includes = true;
    if (selectedTargets.length > 0) {
      includes = this.checkDescendantsForTargets(selectedTargets, result);
      if (selectedTargets.includes('unassigned') && !result.target_id) {
        includes = true;
      }
    }
    return includes;
  };

  buildFilterByBlocked = (option) => {
    const { blockedFilter } = this.state;
    let newBlockedFilter = blockedFilter;
    switch (option) {
      // case yes
      case 1:
        if (newBlockedFilter === 1) {
          newBlockedFilter = -1;
        } else {
          newBlockedFilter = 1;
        }
        break;
      // case no
      case 0:
        if (newBlockedFilter === 0) {
          newBlockedFilter = -1;
        } else {
          newBlockedFilter = 0;
        }
        break;
      default:
        newBlockedFilter = -1;
    }
    this.setState({ blockedFilter: newBlockedFilter }, () => {
      if (!this.props.noPreserve) this.saveComponentState();
    });
  };

  buildDefaultFilter = (filterSelected, filterName, allOptions) => {
    const defaultFilter = this.state[filterName];
    let newDefaultFilter = JSON.parse(JSON.stringify(defaultFilter));

    if (filterSelected === 'all') {
      if (newDefaultFilter.length === allOptions.length + 1) {
        newDefaultFilter = [];
      } else {
        newDefaultFilter = allOptions.map((cadence) => cadence.id);
        newDefaultFilter.push('unassigned');
      }
    } else if (defaultFilter.includes(filterSelected)) {
      newDefaultFilter = defaultFilter.filter((id) => id !== filterSelected);
    } else {
      newDefaultFilter.push(filterSelected);
    }

    this.setState({ [filterName]: newDefaultFilter }, () => {
      if (!this.props.noPreserve) this.saveComponentState();
    });
  };

  handleDefaultFilterClick = (filterName, allOptions) => {
    const defaultFilter = this.state[filterName];
    if (defaultFilter.length === allOptions.length + 1)
      this.setState({ [filterName]: [] });
  };

  checkDescendantsForTargets = (targetIds, result) => {
    const { workPackages } = this.props;

    const descendants = extractImmediateDescendants(
      [result.id],
      workPackages,
      result.level
    );

    return [result, ...descendants]
      .map(
        (descendant) =>
          descendant.target_id && targetIds.includes(descendant.target_id)
      )
      .includes(true);
  };

  buildFilterByState = (event, stateId) => {
    const { stateFilter } = this.state;
    const stateOptions = WorkPackageStatuses;
    let newStateFilter = JSON.parse(JSON.stringify(stateFilter));
    if (event.target && event.target.type === 'button') {
      if (stateFilter.length === Object.keys(stateOptions).length) {
        newStateFilter = [];
      } else {
        newStateFilter = Object.keys(stateOptions).map(
          (option) => stateOptions[option]
        );
      }
    } else if (stateFilter.indexOf(stateId) !== -1) {
      newStateFilter = stateFilter.filter((id) => id !== stateId);
    } else {
      newStateFilter.push(stateId);
    }
    this.setState({ stateFilter: newStateFilter }, () => {
      if (!this.props.noPreserve) this.saveComponentState();
    });
  };

  handleStateFilterClick = () => {
    const { stateFilter } = this.state;
    const stateOptions = WorkPackageStatuses;
    if (stateFilter.length === Object.keys(stateOptions).length)
      this.setState({ stateFilter: [] });
  };

  filterByState = (result) => {
    const { stateFilter } = this.state;
    if (stateFilter.length > 0) {
      const resultState = startCase(toLower(result.state));
      return stateFilter.includes(resultState);
    }
    return true;
  };

  filterByParent = (result) => {
    const { parentFilter } = this.state;
    if (parentFilter.length > 0) {
      return parentFilter.includes(result.parent_id);
    }
    return true;
  };

  filterByTeam = (result) => {
    const { teamFilter } = this.state;
    if (teamFilter.length > 0) {
      return teamFilter.includes(result.team_id);
    }
    return true;
  };

  filterByOwner = (result) => {
    const selectedOwners = this.state.ownerFilter;
    let includes = true;
    if (selectedOwners.length > 0) {
      includes =
        !!result.owner_id &&
        selectedOwners.some((id) => result.owner_id === id);
      if (selectedOwners.includes('unassigned') && !result.owner_id) {
        includes = true;
      }
    }
    return includes;
  };

  filterByBlocked = (result) => {
    const { blockedFilter } = this.state;
    if (blockedFilter !== -1) {
      if (result.blocked && blockedFilter === 1) {
        return true;
      }
      if (!result.blocked && blockedFilter === 0) {
        return true;
      }
      return false;
    }
    return true;
  };

  filterByCreatedBy = (result) => {
    const selectedCreators = this.state.creatorFilter;
    let includes = true;
    if (selectedCreators.length > 0) {
      includes =
        result.creator_id &&
        selectedCreators.some((id) => result.creator_id === id);
      if (selectedCreators.includes('unassigned') && !result.creator_id) {
        includes = true;
      }
    }
    return includes;
  };

  filterByType = (result) => {
    const selectedTypes = this.state.typeFilter;
    let includes = true;
    if (selectedTypes.length > 0) {
      includes =
        result.result_type &&
        result.result_type.organization_level_id &&
        selectedTypes.some(
          (id) => result.result_type.organization_level_id === id
        );
      if (
        selectedTypes.includes('unassigned') &&
        (!result.result_type || !result.result_type.organization_level_id)
      ) {
        includes = true;
      }
    }
    return includes;
  };

  applyFilters = (results) =>
    results
      .filter(this.filterByTeam)
      .filter(this.filterByParent)
      .filter(this.filterByVrs)
      .filter(this.filterBySprint)
      .filter(this.filterByTarget)
      .filter(this.filterByState)
      .filter(this.filterByOwner)
      .filter(this.filterByBlocked)
      .filter(this.filterByCreatedBy)
      .filter(this.filterByType)
      .filter(this.filterByListTeam)
      .filter(this.filterByListProduct);

  headerColumns = (fieldName, field) => {
    const {
      vrsFilter,
      sprintFilter,
      targetFilter,
      stateFilter,
      ownerFilter,
      blockedFilter,
      creatorFilter,
      typeFilter,
      listTeamFilter,
      listProductFilter
    } = this.state;
    let filterClass;
    const { defaultOptions, level, organizationLevels } = this.props;
    const resultTypeOptions = organizationLevels
      .filter((orgLevel) => !orgLevel.deleted_at)
      .map((orgLevel) => ({
        id: orgLevel.id,
        name: orgLevel.name
      }));

    if (['value', 'rroe', 'tc', 'size', 'risk'].includes(fieldName)) {
      const defaultType = ['rroe', 'tc'].includes(fieldName)
        ? 'risk'
        : fieldName;
      let defaults = defaultOptions[`${defaultType}_defaults`];

      if (level > 1) {
        if (fieldName === 'size') {
          defaults = defaultOptions.t_shirt_size_defaults;
        } else if (fieldName === 'value') {
          defaults = defaultOptions.value_defaults;
        } else {
          defaults = defaultOptions.size_defaults;
        }
      }

      const fieldDefaults = defaults ? defaults.values : {};
      filterClass =
        vrsFilter[fieldName] &&
        vrsFilter[fieldName].length > 0 &&
        vrsFilter[fieldName].length !== Object.keys(fieldDefaults).length + 1;

      if (fieldName === 'value' && level === 1) {
        const severityOptions = defaultOptions.severity_defaults
          ? defaultOptions.severity_defaults.values
          : {};
        filterClass =
          vrsFilter[fieldName] &&
          vrsFilter[fieldName].length > 0 &&
          vrsFilter[fieldName].length !==
            Object.keys(fieldDefaults).length +
              Object.keys(severityOptions).length +
              1;

        return (
          <div className={`header-filter ${fieldName}`}>
            {filterClass && (
              <div
                className={`filter-icon ${fieldName}`}
                onClick={() => {
                  vrsFilter[fieldName] = [];
                  this.setState({ vrsFilter: { ...vrsFilter } }, () => {
                    if (!this.props.noPreserve) this.saveComponentState();
                  });
                }}
              />
            )}
            <div className="filter-group">
              <ValueFilterDropdown
                title={field.name}
                valueOptions={fieldDefaults}
                severityOptions={severityOptions}
                buildFilter={this.buildVrsFilter}
                checkedOptions={vrsFilter[fieldName] || []}
                onFilterClick={this.handleVrsFilterClick}
              />
            </div>
          </div>
        );
      }

      return (
        <div className={`header-filter ${fieldName}`}>
          {filterClass && (
            <div
              className={`filter-icon ${fieldName}`}
              onClick={() => {
                const newVrsFilter = JSON.parse(JSON.stringify(vrsFilter));
                newVrsFilter[fieldName] = [];
                this.setState({ vrsFilter: newVrsFilter }, () => {
                  if (!this.props.noPreserve) this.saveComponentState();
                });
              }}
            />
          )}
          <div className="filter-group">
            <VrsFilterDropdown
              title={field.name}
              level={level}
              options={fieldDefaults}
              buildFilter={this.buildVrsFilter}
              checkedOptions={vrsFilter[fieldName] || []}
              onFilterClick={this.handleVrsFilterClick}
            />
          </div>
        </div>
      );
    }
    if (fieldName === 'sprint') {
      filterClass =
        sprintFilter &&
        sprintFilter.length > 0 &&
        sprintFilter.length !== Object.keys(this.props.cadences).length + 1;
      return (
        <div className={`header-filter ${fieldName}`}>
          {filterClass && (
            <div
              className={`filter-icon ${fieldName}`}
              onClick={() =>
                this.setState({ sprintFilter: [] }, () => {
                  if (!this.props.noPreserve) this.saveComponentState();
                })
              }
            />
          )}
          <div className="filter-group">
            <SprintFilter
              type={fieldName}
              options={this.props.cadences}
              filter={this.buildFilterBySprint}
              checkedOptions={sprintFilter}
              onFilterClick={this.handleSprintFilterClick}
            />
          </div>
        </div>
      );
    }
    if (fieldName === 'team') {
      filterClass =
        listTeamFilter &&
        listTeamFilter.length > 0 &&
        listTeamFilter.length !==
          Object.keys(this.props.currentTeams).length + 1;
      return (
        <div className={`header-filter ${fieldName}`}>
          {filterClass && (
            <div
              className={`filter-icon ${fieldName}`}
              onClick={() =>
                this.setState({ listTeamFilter: [] }, () => {
                  if (!this.props.noPreserve) this.saveComponentState();
                })
              }
            />
          )}
          <div className="filter-group">
            <ListFilter
              type={fieldName}
              options={this.props.currentTeams}
              filter={this.buildFilterByListTeam}
              checkedOptions={listTeamFilter}
              onFilterClick={this.handleListTeamFilterClick}
              showUnassigned
            />
          </div>
        </div>
      );
    }
    if (fieldName === 'target') {
      filterClass =
        targetFilter &&
        targetFilter.length > 0 &&
        targetFilter.length !== Object.keys(this.props.targets).length + 1;
      return (
        <div className={`header-filter ${fieldName}`}>
          {filterClass && (
            <div
              className={`filter-icon ${fieldName}`}
              onClick={() =>
                this.setState({ targetFilter: [] }, () => {
                  if (!this.props.noPreserve) this.saveComponentState();
                })
              }
            />
          )}
          <div className="filter-group">
            <TargetFilter
              type={fieldName}
              options={this.props.targets}
              filter={this.buildFilterByTarget}
              checkedOptions={targetFilter}
              classes="btn-link vrs-filter"
              onFilterClick={this.handleTargetFilterClick}
            />
          </div>
        </div>
      );
    }
    if (fieldName === 'state') {
      const stateOptions =
        level === 1 ? WorkPackageStatuses : HigherLevelStatuses;
      filterClass =
        stateFilter &&
        stateFilter.length > 0 &&
        stateFilter.length !== Object.keys(stateOptions).length;
      return (
        <div className={`header-filter ${fieldName}`}>
          {filterClass && (
            <div
              className={`filter-icon ${fieldName}`}
              onClick={() =>
                this.setState({ stateFilter: [] }, () => {
                  if (!this.props.noPreserve) this.saveComponentState();
                })
              }
            />
          )}
          <div className="filter-group">
            <StateFilter
              level={this.props.level}
              filter={this.buildFilterByState}
              options={stateOptions}
              checkedOptions={stateFilter}
              onFilterClick={this.handleStateFilterClick}
            />
          </div>
        </div>
      );
    }
    if (fieldName === 'owner') {
      filterClass =
        ownerFilter &&
        ownerFilter.length > 0 &&
        ownerFilter.length !== Object.keys(this.props.users).length + 1;

      const userOptions = authorizedWorkersForSpace(this.props.users);
      return (
        <div className={`header-filter ${fieldName}`}>
          {filterClass && (
            <div
              className={`filter-icon ${fieldName}`}
              onClick={() =>
                this.setState({ ownerFilter: [] }, () => {
                  if (!this.props.noPreserve) this.saveComponentState();
                })
              }
            />
          )}
          <div className="filter-group">
            <DefaultFilter
              type={fieldName}
              options={userOptions}
              filter={this.buildDefaultFilter}
              checkedOptions={ownerFilter}
              filterName="ownerFilter"
              title={field.name}
              onFilterClick={this.handleDefaultFilterClick}
            />
          </div>
        </div>
      );
    }
    if (fieldName === 'blocked') {
      const blockedOptions = [
        { id: 1, name: 'Yes' },
        { id: 0, name: 'No' }
      ];
      filterClass =
        blockedFilter >= 0 && blockedFilter.length !== blockedOptions.length;
      return (
        <div className={`header-filter ${fieldName}`}>
          {filterClass && (
            <div
              className={`filter-icon ${fieldName}`}
              onClick={() =>
                this.setState({ blockedFilter: -1 }, () => {
                  if (!this.props.noPreserve) this.saveComponentState();
                })
              }
            />
          )}
          <div className="filter-group">
            <DefaultFilter
              type={fieldName}
              options={blockedOptions}
              filter={this.buildFilterByBlocked}
              checkedOptions={[blockedFilter]}
              filterName="blockedFilter"
              title={field.name}
              all={false}
              unassigned={false}
            />
          </div>
        </div>
      );
    }
    if (fieldName === 'created-by') {
      filterClass =
        creatorFilter &&
        creatorFilter.length > 0 &&
        creatorFilter.length !== Object.keys(this.props.users).length + 1;

      const userOptions = this.props.users.filter((user) => {
        if (!user.deleted_at) {
          return true;
        }
        return false;
      });
      // const userOptions = authorizedWorkers(users);

      return (
        <div className={`header-filter ${fieldName}`}>
          {filterClass && (
            <div
              className={`filter-icon ${fieldName}`}
              onClick={() =>
                this.setState({ creatorFilter: [] }, () => {
                  if (!this.props.noPreserve) this.saveComponentState();
                })
              }
            />
          )}
          <div className="filter-group">
            <DefaultFilter
              type={fieldName}
              options={userOptions}
              filter={this.buildDefaultFilter}
              checkedOptions={creatorFilter}
              filterName="creatorFilter"
              title={field.name}
              onFilterClick={this.handleDefaultFilterClick}
            />
          </div>
        </div>
      );
    }
    if (fieldName === 'type') {
      filterClass =
        typeFilter &&
        typeFilter.length > 0 &&
        typeFilter.length !== Object.keys(resultTypeOptions).length + 1;
      return (
        <div className={`header-filter ${fieldName}`}>
          {filterClass && (
            <div
              className={`filter-icon ${fieldName}`}
              onClick={() =>
                this.setState({ typeFilter: [] }, () => {
                  if (!this.props.noPreserve) this.saveComponentState();
                })
              }
            />
          )}
          <div className="filter-group">
            <DefaultFilter
              type={fieldName}
              options={resultTypeOptions}
              filter={this.buildDefaultFilter}
              checkedOptions={typeFilter}
              filterName="typeFilter"
              title={field.name}
              unassigned={false}
              onFilterClick={this.handleDefaultFilterClick}
            />
          </div>
        </div>
      );
    }

    if (fieldName === 'product') {
      filterClass =
        listProductFilter.length &&
        listProductFilter.length !==
          Object.keys(this.props.currentProducts).length + 1;
      return (
        <div className={`header-filter ${fieldName}`}>
          {filterClass && (
            <div
              className={`filter-icon ${fieldName}`}
              onClick={() =>
                this.setState({ listProductFilter: [] }, () => {
                  if (!this.props.noPreserve) this.saveComponentState();
                })
              }
            />
          )}
          <div className="filter-group">
            <ProductFilter
              type={fieldName}
              classes="btn-link vrs-filter"
              options={this.props.currentProducts}
              checkedOptions={listProductFilter}
              filter={this.buildFilterByListProduct}
              onFilterClick={this.handleListProductFilterClick}
              showUnassigned
            />
          </div>
        </div>
      );
    }

    const selected = this.state.sortField === field.source;
    return (
      <div
        type="button"
        className={`table-header ${selected ? 'selected-sort' : ''}`}
        onClick={() => this.activateSort(field.source)}
      >
        <span>{field.name !== 'empty' && field.name} </span>
      </div>
    );
  };

  organizeParentsAndChildren = (workPackages) => {
    const topLevelPackages = workPackages.filter(
      (workPackage) =>
        (this.props.level && workPackage.level === this.props.level) ||
        (!this.props.level && !workPackage.parent_id)
    );
    const topLevelPackageIDs = topLevelPackages.map(
      (workPackage) => workPackage.id
    );
    const descendants = extractImmediateDescendants(
      topLevelPackageIDs,
      workPackages,
      this.props.level || 4
    );
    return [...topLevelPackages, ...descendants];
  };

  render() {
    const {
      workPackages,
      source,
      tableFields,
      alwaysExpanded,
      highlightedIds
    } = this.props;
    const { dropDown, sortField } = this.state;
    const isKanbanTeamSelected = this.props.currentTeams.some(
      (team) => team.kanban
    );
    const tableFieldsWithTeam = isKanbanTeamSelected
      ? tableFields.filter((s) => s.name !== 'Sprint')
      : [...tableFields];

    const sortedResults = this.handleSorting(workPackages);
    const organizedResults = this.organizeParentsAndChildren(sortedResults);
    const theResults = this.applyFilters(organizedResults);

    const visibleFields = tableFieldsWithTeam.filter((field) => field.visible);
    return (
      <>
        <Table
          results={theResults}
          highlightedIds={highlightedIds}
          visibleFields={visibleFields}
          source={source}
          level={this.props.level}
          dropDown={dropDown}
          setDropdown={this.setDropdown}
          headerColumns={this.headerColumns}
          tableFields={tableFields}
          alwaysExpanded={alwaysExpanded}
          collapseLeft={this.props.collapseLeft}
          collapseRight={this.props.collapseRight}
          sortField={sortField}
        />
      </>
    );
  }
}

TableList.propTypes = {
  team: PropTypes.string,
  level: PropTypes.number,
  workPackages: PropTypes.array,
  highlightedIds: PropTypes.array,
  cadences: PropTypes.array,
  defaultOptions: PropTypes.object,
  preservedState: PropTypes.object,
  teams: PropTypes.array,
  products: PropTypes.array,
  currentTeams: PropTypes.array,
  currentProducts: PropTypes.array,
  noPreserve: PropTypes.bool,
  preserveComponentState: PropTypes.func,
  sortList: PropTypes.func,
  alwaysExpanded: PropTypes.bool
};

TableList.defaultProps = {
  currentTeams: [],
  currentProducts: []
};

const mapStateToProps = (state, ownProps) => {
  const { showTrash } = state.trashReducer;
  const workPackages = ownProps.level
    ? workPackagesWithChildren(state, ownProps)
    : state.workPackageReducer.all.filter((wp) => showTrash || !wp.deleted_at);
  const organizationLevels = currentOrganization().organization_levels;
  const cadences = filterActiveCadences(state);
  return {
    team:
      state.userReducer.user.default_team &&
      state.userReducer.user.default_team.short_name,
    workPackages,
    organizationLevels,
    defaultOptions: state.defaultOptionsReducer.defaultOptions,
    cadences,
    teams: state.teamReducer.teams,
    products: state.productReducer.products,
    currentTeams: state.defaultOptionsReducer.currentTeams,
    currentProducts: state.defaultOptionsReducer.currentProducts,
    targets: filterTargetsBySpace(state),
    users: state.userReducer.users
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      sortList,
      setCurrentTeams,
      setCurrentProducts
    },
    dispatch
  );

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TableList)
);
